'use client';

import { useTranslations } from '@dealerportal/i18n/client';
import React, { type FC } from 'react';

/** Maps stable spec IDs to their tooltip translation key path within the 'specs' namespace. */
export const SPEC_TOOLTIP_KEYS: Record<string, string> = {
  acRating: 'tooltips.acRating',
  wearLayerThickness: 'tooltips.wearLayerThickness',
  faceWeight: 'tooltips.faceWeight',
  pileHeight: 'tooltips.pileHeight',
  edgeDetail: 'tooltips.edgeDetail',
  triexta: 'tooltips.triexta',
  goodWeave: 'tooltips.goodWeave',
  oekotex: 'tooltips.oekotex',
  thickness: 'tooltips.thickness',
};

/** Inline label with tooltip — for key-value layouts like the quick view dialog. */
export const SpecLabel: FC<{ specId: string }> = ({ specId }) => {
  const t = useTranslations('specs');
  const [visible, setVisible] = React.useState(false);
  const tooltipId = `spec-label-tooltip-${specId}`;
  const label = t(specId as Parameters<typeof t>[0]);
  const tooltipKey = SPEC_TOOLTIP_KEYS[specId];
  const tooltip = tooltipKey
    ? t(tooltipKey as Parameters<typeof t>[0])
    : undefined;

  return (
    <span className="ui-inline-flex ui-items-center ui-gap-1">
      <span className="ui-uppercase ui-font-bold ui-text-[10px]">{label}</span>
      {tooltip && (
        <button
          type="button"
          aria-label={label}
          aria-describedby={tooltipId}
          className="ui-relative ui-inline-flex ui-items-center ui-text-gray-500 ui-bg-transparent ui-border-0 ui-p-0 ui-cursor-default"
          onMouseEnter={() => setVisible(true)}
          onMouseLeave={() => setVisible(false)}
          onFocus={() => setVisible(true)}
          onBlur={() => setVisible(false)}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setVisible(false);
          }}
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 13 13"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M6.49967 11.9173C9.49122 11.9173 11.9163 9.49219 11.9163 6.50065C11.9163 3.50911 9.49122 1.08398 6.49967 1.08398C3.50813 1.08398 1.08301 3.50911 1.08301 6.50065C1.08301 9.49219 3.50813 11.9173 6.49967 11.9173Z"
              stroke="currentColor"
              strokeWidth="0.947917"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M4.92383 4.87482C5.05118 4.51281 5.30254 4.20754 5.63339 4.0131C5.96424 3.81866 6.35323 3.74758 6.73147 3.81246C7.1097 3.87733 7.45277 4.07398 7.69991 4.36756C7.94705 4.66115 8.08232 5.03273 8.08174 5.41649C8.08174 6.49982 6.45674 7.04149 6.45674 7.04149"
              stroke="currentColor"
              strokeWidth="0.947917"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6.5 9.20898H6.50542"
              stroke="currentColor"
              strokeWidth="0.947917"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span
            id={tooltipId}
            role="tooltip"
            className={`ui-pointer-events-none ui-absolute ui-bottom-full ui-left-1/2 -ui-translate-x-1/2 ui-mb-2 ui-z-[200] ui-w-56 ui-rounded-xl ui-bg-gray-900 ui-shadow-2xl ui-px-3.5 ui-py-3 ui-leading-none${visible ? '' : ' ui-invisible'}`}
          >
            <span className="ui-text-xs ui-text-gray-300 ui-block ui-mb-1 ui-uppercase">
              {label}
            </span>
            <span className="ui-text-xs ui-text-white">{tooltip}</span>
          </span>
        </button>
      )}
    </span>
  );
};

interface Props {
  specId: string;
  value: string;
  className?: string;
  /** When provided the caller manages the tooltip; no bubble is rendered inside the pill. */
  onHover?: (specId: string | null, rect?: DOMRect) => void;
}

export const SpecPill: FC<Props> = ({ specId, value, className, onHover }) => {
  const t = useTranslations('specs');
  const [visible, setVisible] = React.useState(false);
  const ref = React.useRef<HTMLElement>(null);
  const tooltipId = `spec-pill-tooltip-${specId}`;
  const label = t(specId as Parameters<typeof t>[0]);
  const tooltipKey = SPEC_TOOLTIP_KEYS[specId];
  const tooltip = tooltipKey
    ? t(tooltipKey as Parameters<typeof t>[0])
    : undefined;
  const managed = Boolean(onHover);

  const pillClass = `product-card__spec-pill ui-relative ui-inline-flex ui-items-center ui-rounded-full ui-bg-gray-100 ui-px-2.5 ui-py-0.5 ui-text-xs ui-font-medium ui-text-gray-700${tooltip ? ' ui-cursor-default' : ''}${className ? ` ${className}` : ''}`;

  const handleEnter = () => {
    if (managed) {
      onHover!(specId, ref.current?.getBoundingClientRect());
    } else if (tooltip) {
      setVisible(true);
    }
  };

  const handleLeave = () => {
    if (managed) {
      onHover!(null);
    } else {
      setVisible(false);
    }
  };

  if (tooltip || onHover) {
    return (
      <button
        ref={ref as React.RefObject<HTMLButtonElement>}
        type="button"
        aria-label={label}
        aria-describedby={!managed ? tooltipId : undefined}
        className={`${pillClass} ui-border-0 ui-bg-gray-100`}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onFocus={handleEnter}
        onBlur={handleLeave}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setVisible(false);
        }}
      >
        {value}
        {!managed && (
          <span
            id={tooltipId}
            role="tooltip"
            className={`ui-pointer-events-none ui-absolute ui-bottom-full ui-left-1/2 -ui-translate-x-1/2 ui-mb-2 ui-z-[200] ui-w-56 ui-rounded-lg ui-bg-gray-900 ui-px-3 ui-py-2.5 ui-text-xs ui-font-normal ui-text-white ui-shadow-xl${visible ? '' : ' ui-invisible'}`}
          >
            <span className="ui-font-semibold ui-block ui-mb-1">{label}</span>
            {tooltip}
          </span>
        )}
      </button>
    );
  }

  return <span className={pillClass}>{value}</span>;
};
